import { Box, Drawer, Typography } from '@mui/material'
import { C1TypographyVariants } from 'c1ui'
import { COLLAPSED_SIDEBAR_NAV_STORAGE_KEY } from 'components/pages/sidebar/CollapsingSidebar'
import { useLocalStorageContext } from 'hooks/local-storage-context'
import { FC, ReactNode, useEffect, useRef, useState } from 'react'

import { useFlyoutLabelId } from './NavItem'
import { navSidebarOverlayWidth } from './types'

type SidebarOverlayProps = {
    children: ReactNode
    onRef?: (ref: HTMLDivElement | null) => void
    label?: ReactNode
    /** Whether this overlay is persistently shown (not just on hover) */
    isPersistent?: boolean
}

/**
 * Shared base component for large sidebar overlays (Settings, Apps, Groups)
 * Handles positioning and drawer structure.
 *
 * The onRef callback exposes the content wrapper (not the full-height container)
 * so the safe area triangle is sized to actual content, not empty space.
 */
export const SidebarOverlay: FC<SidebarOverlayProps> = ({
    children,
    onRef,
    label,
    isPersistent = false,
}) => {
    const [isCollapsedState] = useLocalStorageContext(COLLAPSED_SIDEBAR_NAV_STORAGE_KEY, false)
    const isCollapsed = isCollapsedState ?? false
    const contentRef = useRef<HTMLDivElement>(null)
    const [left, setLeft] = useState<number | undefined>(undefined)
    const labelId = useFlyoutLabelId()

    useEffect(() => {
        const sidebar = document.getElementById('sidebar')
        const updatePosition = (): void => {
            if (sidebar) {
                setLeft(sidebar.getBoundingClientRect().right)
            }
        }
        updatePosition()

        const resizeObserver = new ResizeObserver(updatePosition)
        if (sidebar) resizeObserver.observe(sidebar)

        window.addEventListener('resize', updatePosition)
        return () => {
            window.removeEventListener('resize', updatePosition)
            resizeObserver.disconnect()
        }
    }, [])

    // Include `left` dependency so this runs after the component renders
    useEffect(() => {
        if (left !== undefined) {
            onRef?.(contentRef.current)
        }
        return () => onRef?.(null)
    }, [onRef, left])

    if (left === undefined) return null

    return (
        <Box
            className="nav-flyout"
            role="menu"
            aria-labelledby={labelId}
            sx={{
                position: 'fixed',
                left,
                top: 0,
                height: '100vh',
            }}
        >
            <Drawer
                variant="permanent"
                PaperProps={{
                    sx: {
                        position: 'relative',
                        width: navSidebarOverlayWidth,
                        height: '100vh',
                        borderTop: '1px solid',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        borderLeft: 'none',
                        backgroundColor: 'background.default',
                        boxShadow: 'none',
                        paddingTop: '44px',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'visible',

                    },
                }}
                sx={{ width: navSidebarOverlayWidth, height: '100vh', overflow: 'visible' }}
            >
                <Box
                    className="nav-flyout-content"
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                    }}
                >
                    <Box ref={contentRef}>
                        <Box className="nav-flyout-animated">
                            {isCollapsed && label && (
                                <Typography
                                    variant="eyebrow"
                                    sx={{ px: 3, pl: 4, pt: 2, fontSize: C1TypographyVariants.caption.fontSize }}
                                    display="block"
                                >
                                    {label}
                                </Typography>
                            )}
                            {children}
                        </Box>
                    </Box>
                </Box>
                {!isPersistent && (
                    <Box
                        className="nav-flyout-shadow"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: '48px',
                            transform: 'translateX(100%)',
                            background: 'linear-gradient(to right, rgba(0, 0, 0, 0.06), transparent)',
                            pointerEvents: 'none',
                        }}
                    />
                )}
            </Drawer>
        </Box>
    )
}
